// src/components/resources/tools/generators/AiChat/AiChatControls.tsx

'use client'

import { useCallback, type MutableRefObject, type RefObject } from 'react'
import { toast } from 'sonner'
import { cn } from '~/lib/utils'
import { useAiChatActions, useAiChatState } from './AiChatContext'
import type { AiProvider, AiTheme, MessageRole } from './AiChat.types'
import { useStepScrollReset } from '~/components/resources/tools/generators/shared/useStepScrollReset'
import { captureAndDownload } from '~/components/resources/tools/generators/shared/exportImage'
import { ExportButton } from '~/components/resources/tools/generators/shared/ExportButton'
import { useExport } from '~/components/resources/tools/generators/shared/useExport'
import { waitForStable } from '~/components/resources/tools/generators/shared/waitForStable'
import { AVATAR_SWATCHES } from '~/components/resources/tools/generators/FakeChat/avatarNameColors'

const DEFAULT_TRACKING_SLUG = 'ai-chat-mockup-generator'

const avatarSwatches: Array<{ label: string; value: string }> = AVATAR_SWATCHES.map(value => {
  const hue = value.replace(/^bg-/, '').replace(/-\d+$/, '')
  return { value, label: hue.charAt(0).toUpperCase() + hue.slice(1) }
})

const providerLabels: Record<AiProvider, string> = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  gemini: 'Gemini',
}

const providerDescriptions: Record<AiProvider, string> = {
  chatgpt: 'OpenAI ChatGPT — grey user bubbles, the blossom mark, and the response action bar.',
  claude: 'Anthropic Claude — warm ivory canvas, cream user bubbles, and the clay starburst mark.',
  gemini: 'Google Gemini — tonal user bubbles and the four-point gradient spark.',
}

const themeOptions: Array<{ value: AiTheme; label: string }> = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

const roleOptions: Array<{ value: MessageRole; label: string }> = [
  { value: 'user', label: 'User (you)' },
  { value: 'assistant', label: 'Assistant' },
]

interface AiChatControlsProps {
  previewRef: RefObject<HTMLDivElement | null>
  previewStaleRef?: MutableRefObject<boolean>
  trackingSlug?: string
}

const inputClass =
  'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-indigo-400 dark:focus:ring-indigo-900'
const labelTextClass = 'text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400'

export function AiChatControls({ previewRef, previewStaleRef, trackingSlug = DEFAULT_TRACKING_SLUG }: AiChatControlsProps): JSX.Element {
  const { settings, messages, activeMessageId, isExporting } = useAiChatState()
  const actions = useAiChatActions()
  // Reuse the shared export lifecycle for the html2canvas warm-on-mount and the
  // synchronous re-entrancy guard; the VISIBLE busy flag stays the context's
  // `isExporting` (driven via `actions.setExporting`).
  const { runExport } = useExport()

  const scrollContainerRef = useStepScrollReset<HTMLElement>(0, { offset: 96 })

  const handleExport = useCallback((): Promise<void> => runExport(async () => {
    if (!previewRef.current) {
      toast.error('Preview not found', { description: 'Please try again in a moment.' })
      return
    }
    if (previewStaleRef) {
      const stabilized = await waitForStable(() => !previewStaleRef.current)
      if (!stabilized) {
        toast.error('Preview is still updating — pause a moment, then export again.')
        return
      }
    }

    // Flip the busy flag only AFTER the gate so it tracks the capture.
    actions.setExporting(true)
    try {
      await captureAndDownload({
        element: previewRef.current,
        format: 'png',
        filename: `${settings.provider}-chat-mockup-${Date.now()}`,
        trackingSlug,
        extraParams: { provider: settings.provider, theme: settings.theme },
        expandScrollableContent: true,
        onSuccess: () => toast.success('Download started', { description: 'Your AI chat mockup PNG is downloading.' }),
        onError: () => toast.error('Export failed', { description: 'Could not generate image. Please try again.' }),
      })
    } finally {
      actions.setExporting(false)
    }
  }), [runExport, previewRef, previewStaleRef, actions, settings.provider, settings.theme, trackingSlug])

  const activeMessage = messages.find(message => message.id === activeMessageId)

  const handleAddMessage = (): void => {
    const last = messages[messages.length - 1]
    // Alternate role from the last turn so a conversation builds naturally.
    const role: MessageRole = last?.role === 'user' ? 'assistant' : 'user'
    const id = actions.generateMessageId()
    actions.addMessage({
      id,
      role,
      content: role === 'user' ? 'Ask a follow-up question…' : 'Here is the assistant response…',
    })
  }

  return (
    <aside
      ref={scrollContainerRef}
      className="overflow-hidden rounded-3xl border border-slate-200 bg-white/80 shadow-xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/70 lg:max-h-[calc(100dvh-8rem)] lg:overflow-y-auto"
    >
      <div className="flex flex-col px-6 pb-6">
        {/* Export */}
        <section aria-labelledby="ai-chat-export" className="space-y-3 pt-6">
          <h2 id="ai-chat-export" className={cn('text-sm font-semibold', labelTextClass)}>
            Export
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Download a retina-density PNG of your AI chat mockup, ready for decks, demos, and social proof.
          </p>
          <ExportButton
            isExporting={isExporting}
            onExport={() => void handleExport()}
            icon={null}
            className="w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-300 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          />
        </section>

        {/* Assistant */}
        <section aria-labelledby="ai-chat-provider" className="space-y-4 pt-8">
          <div className="flex items-center justify-between">
            <h2 id="ai-chat-provider" className={cn('text-sm font-semibold', labelTextClass)}>
              Assistant
            </h2>
            <button
              type="button"
              onClick={() => actions.resetConversation()}
              className="text-xs font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
            >
              Reset preset
            </button>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {(Object.keys(providerLabels) as AiProvider[]).map(provider => {
              const isActive = settings.provider === provider
              return (
                <button
                  key={provider}
                  type="button"
                  onClick={() => actions.setProvider(provider)}
                  className={cn(
                    'flex flex-col items-start rounded-2xl border px-4 py-3 text-left transition-all',
                    isActive
                      ? 'border-indigo-500/70 bg-indigo-50 text-indigo-700 dark:border-indigo-400/60 dark:bg-indigo-500/20 dark:text-indigo-200'
                      : 'border-slate-200 bg-white hover:border-indigo-200 hover:text-indigo-600 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-700/40 dark:hover:text-indigo-300',
                  )}
                >
                  <span className="text-sm font-semibold">{providerLabels[provider]}</span>
                  <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">{providerDescriptions[provider]}</span>
                </button>
              )
            })}
          </div>
        </section>

        {/* Appearance */}
        <section aria-labelledby="ai-chat-appearance" className="space-y-4 pt-8">
          <h2 id="ai-chat-appearance" className={cn('text-sm font-semibold', labelTextClass)}>
            Appearance
          </h2>
          <div className="space-y-2">
            <span className={labelTextClass}>Theme</span>
            <div className="grid grid-cols-2 gap-2">
              {themeOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => actions.updateSettings({ theme: option.value })}
                  className={cn(
                    'rounded-xl border px-4 py-2 text-sm font-medium transition',
                    settings.theme === option.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400/60 dark:bg-indigo-500/20 dark:text-indigo-200'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-200 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <label className="block space-y-2">
            <span className={labelTextClass}>Model label</span>
            <input
              type="text"
              value={settings.modelLabel}
              onChange={event => actions.updateSettings({ modelLabel: event.target.value })}
              className={inputClass}
              placeholder="ChatGPT 4o"
            />
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={settings.showHeader}
                onChange={event => actions.updateSettings({ showHeader: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Header
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={settings.showAvatars}
                onChange={event => actions.updateSettings({ showAvatars: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Avatars
            </label>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={settings.showActions}
                onChange={event => actions.updateSettings({ showActions: event.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Action bar
            </label>
          </div>
        </section>

        {/* Identity */}
        <section aria-labelledby="ai-chat-identity" className="space-y-4 pt-8">
          <h2 id="ai-chat-identity" className={cn('text-sm font-semibold', labelTextClass)}>
            Your avatar
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            These three assistant UIs label the user only by an initials avatar — set its initials and color.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-2">
              <span className={labelTextClass}>Avatar initials</span>
              <input
                type="text"
                maxLength={2}
                value={settings.userInitials}
                onChange={event => actions.updateSettings({ userInitials: event.target.value.toUpperCase().slice(0, 2) })}
                className={inputClass}
                placeholder="JD"
              />
            </label>
            <label className="block space-y-2">
              <span className={labelTextClass}>Avatar color</span>
              <select
                value={settings.userAvatarColor}
                onChange={event => actions.updateSettings({ userAvatarColor: event.target.value })}
                className={inputClass}
              >
                {avatarSwatches.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>

        {/* Conversation */}
        <section aria-labelledby="ai-chat-messages" className="space-y-4 pt-8">
          <div className="flex items-center justify-between">
            <h2 id="ai-chat-messages" className={cn('text-sm font-semibold', labelTextClass)}>
              Conversation
            </h2>
            <button
              type="button"
              onClick={handleAddMessage}
              className="rounded-full border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-600 transition hover:bg-indigo-50 dark:border-indigo-500/40 dark:text-indigo-300 dark:hover:bg-indigo-500/20"
            >
              Add turn
            </button>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Edit the prompt-and-response transcript. Assistant turns support Markdown — <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">**bold**</code>, lists, headings, and fenced code blocks.
          </p>
          <div className="space-y-2">
            {messages.map((message, index) => {
              const isActive = activeMessageId === message.id
              return (
                <div
                  key={message.id}
                  className={cn(
                    'flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition',
                    isActive
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400/60 dark:bg-indigo-500/10 dark:text-indigo-200'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-200 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300',
                  )}
                >
                  <button type="button" onClick={() => actions.setActiveMessage(message.id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                    <span className={cn('rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide', message.role === 'user' ? 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300')}>
                      {message.role === 'user' ? 'You' : 'AI'}
                    </span>
                    <span className="line-clamp-1 flex-1 text-xs text-slate-500 dark:text-slate-400">{message.content}</span>
                  </button>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => actions.moveMessage(message.id, 'up')}
                      disabled={index === 0}
                      className="rounded p-1 text-slate-400 hover:text-indigo-600 disabled:opacity-30"
                      aria-label="Move turn up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.moveMessage(message.id, 'down')}
                      disabled={index === messages.length - 1}
                      className="rounded p-1 text-slate-400 hover:text-indigo-600 disabled:opacity-30"
                      aria-label="Move turn down"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          {activeMessage ? (
            <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/70">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Editing turn</p>
                <button
                  type="button"
                  onClick={() => actions.removeMessage(activeMessage.id)}
                  disabled={messages.length <= 1}
                  className="text-xs font-medium text-rose-500 hover:text-rose-600 disabled:opacity-40 dark:text-rose-300"
                >
                  Delete turn
                </button>
              </div>
              <label className="mt-3 block space-y-1 text-xs text-slate-500 dark:text-slate-400">
                Speaker
                <select
                  value={activeMessage.role}
                  onChange={event => actions.updateMessage(activeMessage.id, { role: event.target.value as MessageRole })}
                  className={inputClass}
                >
                  {roleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mt-3 block space-y-2 text-xs text-slate-500 dark:text-slate-400">
                Message content
                <textarea
                  value={activeMessage.content}
                  onChange={event => actions.updateMessage(activeMessage.id, { content: event.target.value })}
                  rows={6}
                  className={cn(inputClass, 'font-mono text-[13px] leading-relaxed')}
                />
              </label>
            </div>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400">Select a turn to edit its content.</p>
          )}
        </section>
      </div>
    </aside>
  )
}
